import { Box, Stack, Typography } from '@mui/material'
import DOMPurify from 'dompurify'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { Alert, COLORS, optimizelyCss, unit } from '@hcr/ui'
import { isNotUndefined, isUndefined } from '@hcr/utils'

import { useBenefitDetailsQuery } from '../../../hooks'
import type { OwnerBenefitDetailsPathParams } from '../../../models'
import { Metadata } from '../../common/metadata'
import { Owner } from '../owner/owner'

import { OwnerBenefitDetailsDrawer } from './owner-benefit-details-drawer'
import { OwnerBenefitDetailsLoading } from './OwnerBenefitDetailsLoading'

export const OwnerBenefitDetails: FC = () => {
  const params = useParams<OwnerBenefitDetailsPathParams>()
  const { t } = useTranslation()

  const benefitDetails = useBenefitDetailsQuery(
    { benefitId: String(params.benefitId) },
    { enabled: isNotUndefined(params.benefitId) }
  )

  if (isUndefined(params.benefitId) || benefitDetails.isError) {
    return (
      <>
        <Owner />
        <OwnerBenefitDetailsDrawer>
          <Box padding={unit(5)}>
            <Alert severity='error' title={t('error.error')} description={t('error.generic')} />
          </Box>
        </OwnerBenefitDetailsDrawer>
      </>
    )
  }

  if (benefitDetails.isSuccess) {
    return (
      <>
        <Owner />
        <OwnerBenefitDetailsDrawer>
          <Metadata title={t('page-title.owner-benefit-details')} />
          <Stack
            gap={unit(0)}
            paddingBottom={unit(10)}
            paddingTop={unit(0)}
            paddingLeft={unit(7.75)}
            paddingRight={unit(12)}
          >
            <Stack direction='column' gap={unit(0.5)} paddingBottom={unit(3)}>
              <Typography component='h1' variant='titleM'>
                {benefitDetails.data.title}
              </Typography>
              <Typography component='h2' variant='headlineL' color={COLORS.burgundi[500]}>
                {benefitDetails.data.subtitle}
              </Typography>
            </Stack>

            <Typography
              variant='bodyM'
              css={optimizelyCss}
              paddingBottom={unit(2)}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(benefitDetails.data.description) }}
            />
          </Stack>
        </OwnerBenefitDetailsDrawer>
      </>
    )
  }

  return (
    <>
      <Owner />
      <OwnerBenefitDetailsLoading />
    </>
  )
}
